import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { sendGlobalBroadcastAPI } from '../services/serverService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';

const GlobalBroadcastModal = ({ closeModal, servers }) => {
    const { currentUser } = useAuth();
    
    // States
    const [selectedServerIds, setSelectedServerIds] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [attachedFile, setAttachedFile] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        audience: 'All Staff',
        requireReadReceipt: false
    });

    const AUDIENCE_OPTIONS = ['All Staff', 'Employees Only', 'Admins Only', 'Service Users Only', 'All Users'];

    // --- HANDLERS ---
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedServerIds(servers.map(s => s.server_id));
        } else {
            setSelectedServerIds([]);
        }
    };

    const handleToggleServer = (serverId) => {
        setSelectedServerIds(prev => 
            prev.includes(serverId) ? prev.filter(id => id !== serverId) : [...prev, serverId]
        );
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (selectedServerIds.length === 0) return alert("Please select at least one server.");
        if (!formData.message.trim()) return alert("Message cannot be empty.");

        const confirmSend = window.confirm(`WARNING: You are about to blast this to ${selectedServerIds.length} server(s).\n\nAre you absolutely sure?`);
        if (!confirmSend) return;

        setIsSending(true);

        try {
            let documentUrl = "";

            if (attachedFile) {
                const fileRef = ref(storage, `admins/${currentUser.uid}/global_broadcasts/${Date.now()}_${attachedFile.name.replace(/\s+/g, '_')}`);
                await uploadBytes(fileRef, attachedFile);
                documentUrl = await getDownloadURL(fileRef);
            }

            const payload = {
                targetServerIds: selectedServerIds,
                title: formData.title.trim() || 'Global Announcement',
                message: formData.message.trim(),
                audience: formData.audience,
                documentUrl: documentUrl,
                requireReadReceipt: formData.requireReadReceipt
            };

            await sendGlobalBroadcastAPI(payload);
            
            alert(`Success! Global Broadcast fanned out to ${selectedServerIds.length} servers.`);
            closeModal();
            
        } catch (error) {
            console.error("Global Broadcast failed:", error);
            alert("Failed to send broadcast. Check console.");
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }} onClick={closeModal}>
            <div style={{ backgroundColor: 'white', width: '900px', maxWidth: '95vw', borderRadius: '12px', display: 'flex', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }} onClick={e => e.stopPropagation()}>
                
                {/* LEFT PANE: SERVER SELECTION */}
                <div style={{ width: '40%', backgroundColor: '#f7fafc', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#edf2f7' }}>
                        <h3 style={{ margin: '0 0 10px 0', color: '#2d3748', fontSize: '16px' }}>Select Target Servers</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'white', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e0' }}>
                            <input 
                                type="checkbox" 
                                id="selectAll"
                                checked={selectedServerIds.length === servers.length && servers.length > 0}
                                onChange={handleSelectAll}
                                style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                            />
                            <label htmlFor="selectAll" style={{ fontWeight: 'bold', color: '#4a5568', cursor: 'pointer', margin: 0 }}>
                                Select All Active Servers ({servers.length})
                            </label>
                        </div>
                    </div>
                    
                    <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
                        {servers.map(server => (
                            <label key={server.server_id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 10px', cursor: 'pointer', borderRadius: '6px', transition: 'background 0.2s', backgroundColor: selectedServerIds.includes(server.server_id) ? '#ebf8ff' : 'transparent', borderBottom: '1px solid #edf2f7' }}>
                                <input 
                                    type="checkbox" 
                                    checked={selectedServerIds.includes(server.server_id)}
                                    onChange={() => handleToggleServer(server.server_id)}
                                    style={{ transform: 'scale(1.2)' }}
                                />
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontWeight: '600', color: '#2d3748', fontSize: '14px' }}>{server.server_name}</span>
                                    <span style={{ fontSize: '11px', color: '#a0aec0' }}>ID: {server.server_id}</span>
                                </div>
                            </label>
                        ))}
                    </div>
                    <div style={{ padding: '15px', borderTop: '1px solid #e2e8f0', fontSize: '13px', fontWeight: 'bold', color: '#3182ce', textAlign: 'center' }}>
                        {selectedServerIds.length} Server(s) Selected
                    </div>
                </div>

                {/* RIGHT PANE: COMPOSE MESSAGE */}
                <div style={{ width: '60%', padding: '30px', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h2 style={{ margin: 0, color: '#2d3748', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '20px' }}><span>📢</span> Global Megaphone</h2>
                        <button onClick={closeModal} style={{ background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#a0aec0' }}>✖</button>
                    </div>

                    <form onSubmit={handleSubmit} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                            <div style={{ flex: 2 }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#4a5568' }}>Subject / Title</label>
                                <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="System Maintenance..." style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e0', boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#4a5568' }}>Audience</label>
                                <select name="audience" value={formData.audience} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e0', backgroundColor: '#f7fafc', boxSizing: 'border-box' }}>
                                    {AUDIENCE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                            </div>
                        </div>

                        <div style={{ marginBottom: '15px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#4a5568' }}>Message</label>
                            <textarea name="message" value={formData.message} onChange={handleChange} required placeholder="Type your global announcement here..." style={{ flex: 1, width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e0', resize: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}></textarea>
                        </div>

                        <div style={{ backgroundColor: '#f7fafc', padding: '12px', borderRadius: '6px', border: '1px dashed #a0aec0', marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#4a5568' }}>📎 Attach File (Optional)</label>
                            <input type="file" onChange={(e) => setAttachedFile(e.target.files[0])} style={{ fontSize: '13px' }} accept="image/*,.pdf,.doc,.docx" />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', padding: '10px', backgroundColor: '#fffaf0', border: '1px solid #feebc8', borderRadius: '6px' }}>
                            <input type="checkbox" id="global-read-receipt" name="requireReadReceipt" checked={formData.requireReadReceipt} onChange={handleChange} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                            <label htmlFor="global-read-receipt" style={{ fontSize: '13px', color: '#975a16', cursor: 'pointer', margin: 0, fontWeight: '500' }}>Require client apps to enforce read receipt acknowledgment.</label>
                        </div>

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: 'auto' }}>
                            <button type="button" onClick={closeModal} style={{ padding: '12px 20px', backgroundColor: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Cancel</button>
                            <button type="submit" disabled={isSending} style={{ padding: '12px 24px', backgroundColor: '#e53e3e', color: 'white', border: 'none', borderRadius: '6px', fontSize: '15px', fontWeight: 'bold', cursor: isSending ? 'not-allowed' : 'pointer', boxShadow: '0 4px 6px rgba(229, 62, 62, 0.2)' }}>
                                {isSending ? 'Transmitting...' : 'BLAST TO SERVERS'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default GlobalBroadcastModal;